import { captureException } from '@sentry/browser';
import { VideoQuality } from '@zoom/videosdk';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';

import { BackgroundConstants } from '../VirtualBackgroundModal/VirtualBackgroundModal';

export const STATUS = {
  joining: 'joining',
  startingCameara: 'startingCameara',
  incall: 'incall',
};

function useStartZoomSdk({
  zoomClient,
  zoomInfo,
  selfVideoContainerId,
  userName,
  showError,
  logEvents,
}) {
  const [status, setStatus] = useState(null);
  const getCurrentUserId = useCallback(
    () => zoomClient?.getCurrentUserInfo()?.userId,
    [zoomClient],
  );

  useEffect(() => {
    const startAudio = async stream => {
      const startVideoTime = Date.now();
      try {
        await stream.startAudio();
      } catch (error) {
        captureException(new Error(error?.type), {
          extra: {
            startTime: startVideoTime,
            duration: moment.duration(moment().diff(startVideoTime)).asSeconds(),
            zoomInfo,
          },
        });
        showError(`Failed to start audio (consultId: ${zoomInfo?.consultId})`);
      }
    };
    const attachVideo = async (stream, currentUserId) => {
      const startVideoTime = Date.now();
      try {
        const video = await stream.attachVideo(currentUserId, VideoQuality.Video_360P);
        const videoElement = document.getElementById(selfVideoContainerId);
        videoElement.innerHTML = '';
        videoElement.appendChild(video);
      } catch (error) {
        captureException(new Error(error?.type), {
          extra: {
            startTime: startVideoTime,
            duration: moment.duration(moment().diff(startVideoTime)).asSeconds(),
            zoomInfo,
          },
        });
        showError(`Failed to show video (consultId: ${zoomInfo?.consultId})`);
      }
    };

    const startVideo = async (stream, currentUserId) => {
      const bgImage = localStorage.getItem('video-call-background');
      const startVideoTime = Date.now();
      try {
        setStatus(STATUS.startingCameara);
        if (!bgImage || bgImage === BackgroundConstants.NONE) await stream.startVideo();
        else await stream.startVideo({ virtualBackground: { imageUrl: bgImage } });
        await attachVideo(stream, currentUserId);
      } catch (error) {
        captureException(new Error(error?.type), {
          extra: {
            startTime: startVideoTime,
            duration: moment.duration(moment().diff(startVideoTime)).asSeconds(),
            zoomInfo,
          },
        });
        showError(`Failed to start video (consultId: ${zoomInfo?.consultId})`);
      }
      setStatus(null);
    };

    async function startVideoCallFlow() {
      const { sessionName, doctorToken } = zoomInfo ?? {};
      if (zoomInfo && sessionName && doctorToken && zoomClient) {
        setStatus(STATUS.joining);
        const startJoinTime = Date.now();
        try {
          await zoomClient.join(sessionName, doctorToken, userName, undefined, 1);
          const { sessionId } = zoomClient.getSessionInfo();
          const userInfo = zoomClient?.getCurrentUserInfo();
          logEvents({
            event: 'session.user_joined',
            payload: {
              object: {
                id: sessionId,
                sessionId,
                user: {
                  name: userInfo?.displayName,
                  id: `${userInfo?.userId}`,
                  userKey: userInfo?.userIdentity,
                },
                sessionName,
              },
            },
          });
          const stream = zoomClient.getMediaStream();
          const currentUserId = getCurrentUserId();
          startVideo(stream, currentUserId);
          startAudio(stream);
        } catch (error) {
          captureException(new Error(error?.type), {
            extra: {
              startTime: startJoinTime,
              duration: moment.duration(moment().diff(startJoinTime)).asSeconds(),
              zoomInfo,
            },
          });
          showError('Failed to join zoom call');
        } finally {
          // setStatus(STATUS.incall);
        }
      }
    }
    startVideoCallFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomInfo, userName, selfVideoContainerId, zoomClient, getCurrentUserId]);
  return { status };
}

export default useStartZoomSdk;
